import React, { useCallback, useRef, useState } from "react"
import { useMutation, useQuery } from "@apollo/client/react"
import { useNavigate, useParams } from "react-router-dom"
import { ReactFlowProvider, useEdgesState, useNodesState } from "@xyflow/react"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { useAppDispatch } from "lib/store"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { WorkflowEdge, WorkflowNode, WorkflowNodeData } from "lib/types/workflow"
import { WorkflowApiNode } from "lib/types/workflowConditions"
import { apiJsonToFlow, flowToApiJson } from "helpers/workflowConverter"
import { extractWorkflowErrorMessage } from "helpers/workflowErrors"

import {
  CreateWorkflowDocument,
  CreateWorkflowMutation,
  CreateWorkflowMutationVariables,
  CreateWorkflowTriggerDocument,
  CreateWorkflowTriggerMutation,
  CreateWorkflowTriggerMutationVariables,
  DeleteWorkflowDocument,
  DeleteWorkflowMutation,
  DeleteWorkflowMutationVariables,
  DeleteWorkflowTriggerDocument,
  DeleteWorkflowTriggerMutation,
  DeleteWorkflowTriggerMutationVariables,
  GetWorkflowDocument,
  GetWorkflowQuery,
  GetWorkflowQueryVariables,
  OrderWorkflowTriggerType,
  SearchOrderWorkflowExecutionsDocument,
  SearchOrderWorkflowExecutionsQuery,
  SearchOrderWorkflowExecutionsQueryVariables
} from "lib/types/generated/graphql"

import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import WorkflowCanvas from "components/Workflow/Canvas/WorkflowCanvas"
import NodeEditorSidebar from "components/Workflow/NodeEditor/NodeEditorSidebar"
import DeleteWorkflow from "components/Workflow/DeleteWorkflow"
import PublishWorkflow from "components/Workflow/PublishWorkflow"
import ExecutionTable from "components/Workflow/ExecutionTable"
import { BrinkLoader } from "components/Ui/BrinkLoader"
import { WithAuth } from "components/Auth"
import { PermissionGroups } from "routes/constants"

import { ReactComponent as ArrowLeft } from "images/icons/arrow-left.svg"

import {
  BackLink,
  ButtonGroup,
  CanvasSection,
  Container,
  DraftIndicator,
  Header,
  NameInput,
  StatusBadge,
  TabsContainer
} from "./EditWorkflow.styled"

export const EditWorkflow = () => {
  const { id: workflowId } = useParams<{ id: string }>()
  const [workflowName, setWorkflowName] = useState("")
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>([])
  const [revision, setRevision] = useState(0)
  const [publishedRevision, setPublishedRevision] = useState<number | null>(null)
  const [triggerType, setTriggerType] = useState<string>(OrderWorkflowTriggerType.OrderCreated)
  const [executionsCount, setExecutionsCount] = useState<number | null>(null)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initialStateRef = useRef<string>("")

  const serializeState = useCallback(
    (name: string, n: WorkflowNode[], e: WorkflowEdge[]) =>
      JSON.stringify({ name, nodes: flowToApiJson(n, e) }),
    []
  )

  const { loading: queryLoading, data } = useQuery<GetWorkflowQuery, GetWorkflowQueryVariables>(
    GetWorkflowDocument,
    {
      variables: { workflowId: workflowId! },
      skip: !workflowId
    }
  )

  const { data: executionsCountData } = useQuery<
    SearchOrderWorkflowExecutionsQuery,
    SearchOrderWorkflowExecutionsQueryVariables
  >(SearchOrderWorkflowExecutionsDocument, {
    variables: {
      from: 0,
      size: 0,
      query: JSON.stringify({ bool: { must: [{ term: { workflowId: workflowId! } }] } })
    },
    skip: !workflowId,
    fetchPolicy: "network-only"
  })

  const displayedExecutionsCount =
    executionsCount ?? executionsCountData?.searchOrderWorkflowExecutions?.total

  // Handle workflow data when it's loaded
  React.useEffect(() => {
    const workflow = data?.Order?.workflow
    if (workflow) {
      setWorkflowName(workflow.name)
      setRevision(workflow.revision)
      setPublishedRevision(workflow.published?.revision ?? null)
      setTriggerType(workflow.trigger ?? OrderWorkflowTriggerType.OrderCreated)
      const apiNodes: WorkflowApiNode[] =
        typeof workflow.then === "string" ? JSON.parse(workflow.then) : (workflow.then ?? [])
      const flow = apiJsonToFlow(apiNodes, workflow.trigger)
      setNodes(flow.nodes)
      setEdges(flow.edges)
      initialStateRef.current = serializeState(workflow.name, flow.nodes, flow.edges)
    }
  }, [data, setNodes, setEdges, serializeState])

  const snapshotRef = useRef<string>("")

  const [createWorkflow, { loading: saving }] = useMutation<
    CreateWorkflowMutation,
    CreateWorkflowMutationVariables
  >(CreateWorkflowDocument, {
    update: (cache, _result, { variables }) => {
      if (!variables?.workflowId) return
      cache.modify({
        id: cache.identify({ __typename: "OrderWorkflow", id: variables.workflowId }),
        fields: {
          name: () => variables.input.name,
          trigger: () => variables.input.trigger,
          then: () => variables.input.then
        }
      })
    },
    onCompleted: () => {
      setRevision((r) => r + 1)
      initialStateRef.current = snapshotRef.current
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow saved successfully",
          type: "success"
        })
      )
    },
    onError: (error) => {
      dispatch(
        alertActions.actions.createAlert({
          message: extractWorkflowErrorMessage(error) ?? "Failed to save workflow",
          type: "error"
        })
      )
    }
  })

  const [deleteWorkflow, { loading: deleting }] = useMutation<
    DeleteWorkflowMutation,
    DeleteWorkflowMutationVariables
  >(DeleteWorkflowDocument, {
    update: (cache, _result, { variables }) => {
      if (!variables?.workflowId) return
      cache.evict({
        id: cache.identify({ __typename: "OrderWorkflow", id: variables.workflowId })
      })
      cache.evict({
        id: cache.identify({ __typename: "OrderWorkflowSearchHit", id: variables.workflowId })
      })
      cache.gc()
    },
    onCompleted: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow deleted",
          type: "success"
        })
      )
      navigate("/settings/workflows/")
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Failed to delete workflow",
          type: "error"
        })
      )
    }
  })

  const [createTrigger, { loading: publishing }] = useMutation<
    CreateWorkflowTriggerMutation,
    CreateWorkflowTriggerMutationVariables
  >(CreateWorkflowTriggerDocument, {
    onCompleted: () => {
      setPublishedRevision(revision)
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow published successfully",
          type: "success"
        })
      )
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Failed to publish workflow",
          type: "error"
        })
      )
    }
  })

  const [deleteTrigger] = useMutation<
    DeleteWorkflowTriggerMutation,
    DeleteWorkflowTriggerMutationVariables
  >(DeleteWorkflowTriggerDocument, {
    onCompleted: () => {
      setPublishedRevision(null)
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow unpublished",
          type: "success"
        })
      )
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Failed to unpublish workflow",
          type: "error"
        })
      )
    }
  })

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      setSelectedNode(node)
      dispatch(showEditSidebar())
    },
    [dispatch]
  )

  const handleNodeSave = useCallback(
    (nodeId: string, data: WorkflowNodeData) => {
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data } : n)))
      setSelectedNode(null)
    },
    [setNodes]
  )

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
      setSelectedNode(null)
    },
    [setNodes, setEdges]
  )

  const handleSave = () => {
    if (!workflowId) return
    const apiNodes = flowToApiJson(nodes, edges)
    const snapshot = serializeState(workflowName, nodes, edges)
    snapshotRef.current = snapshot

    createWorkflow({
      variables: {
        workflowId,
        input: {
          name: workflowName,
          trigger: triggerType as OrderWorkflowTriggerType,
          then: JSON.stringify(apiNodes)
        }
      }
    })
  }

  const handleDelete = () => {
    if (!workflowId) return
    deleteWorkflow({ variables: { workflowId } })
  }

  const handlePublish = () => {
    if (!workflowId) return
    createTrigger({
      variables: { workflowId, revision }
    })
  }

  const handleUnpublish = () => {
    if (!workflowId) return
    deleteTrigger({ variables: { workflowId } })
  }

  if (queryLoading) return <BrinkLoader />

  const isPublished = publishedRevision !== null
  const hasDraft = isPublished && revision > publishedRevision
  const hasChanges =
    initialStateRef.current !== "" &&
    initialStateRef.current !== serializeState(workflowName, nodes, edges)

  return (
    <Container>
      <Header>
        <BackLink onClick={() => navigate("/settings/workflows/")}>
          <ArrowLeft /> Back
        </BackLink>
        <StatusBadge>Rev {revision}</StatusBadge>
        <StatusBadge $published={isPublished}>{isPublished ? "Published" : "Draft"}</StatusBadge>
        {hasDraft && <DraftIndicator>v{revision} unpublished</DraftIndicator>}
        <NameInput
          aria-label="Workflow name"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          placeholder="Workflow name"
          maxLength={100}
        />
        <ButtonGroup>
          <WithAuth requiredGroups={PermissionGroups.workflows.write}>
            <DeleteWorkflow onDelete={handleDelete} loading={deleting} />
            <PublishWorkflow
              isPublished={isPublished}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              loading={publishing}
              hasDraft={hasDraft}
              draftRevision={revision}
            />
            <PrimaryButton handleClick={handleSave} loading={saving} disabled={!hasChanges}>
              Save
            </PrimaryButton>
          </WithAuth>
        </ButtonGroup>
      </Header>
      <TabsContainer>
        <Tabs>
          <TabList>
            <Tab>Editor</Tab>
            <Tab>
              Executions
              {displayedExecutionsCount !== undefined &&
                displayedExecutionsCount !== null && (
                  <b> ({displayedExecutionsCount})</b>
                )}
            </Tab>
          </TabList>
          <TabPanel>
            <ReactFlowProvider>
              <CanvasSection>
                <WorkflowCanvas
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  setNodes={setNodes}
                  setEdges={setEdges}
                  onNodeClick={handleNodeClick}
                />
              </CanvasSection>
            </ReactFlowProvider>
          </TabPanel>
          <TabPanel>
            {workflowId && (
              <ExecutionTable
                workflowId={workflowId}
                onTotalChange={setExecutionsCount}
              />
            )}
          </TabPanel>
        </Tabs>
      </TabsContainer>
      <NodeEditorSidebar
        selectedNode={selectedNode}
        onSave={handleNodeSave}
        onDelete={handleNodeDelete}
      />
    </Container>
  )
}
